// Agency-level analytics dashboard.
//
// Three headline views:
//   1. Where is my money coming from? — Close rate by source, sorted
//      by volume with color-coded conversion rate.
//   2. How fast are we hunting? — Speed-to-contact p50/p90 by source,
//      red bar when p50 > 5min or over_5min_pct > 50%.
//   3. Am I about to lose the business? — MRR concentration by
//      carrier with cliff alerts (WARN ≥40%, CRIT ≥60%).

import React from 'react';
import { readPersistedJwtDirect } from '../lib/authedFetch';
import { supabase } from '../lib/supabase';
import { useIsDemoMode, useDemoLeads, useDemoHealthPolicies } from '../demo/DemoDataProvider';

async function apiFetch(path) {
  const jwt = readPersistedJwtDirect();
  const res = await fetch(path, {
    headers: {
      'content-type': 'application/json',
      ...(jwt ? { authorization: `Bearer ${jwt}` } : {}),
    },
  });
  const body = await res.json().catch(() => ({}));
  return { ok: res.ok, status: res.status, body };
}

function fmtDuration(seconds) {
  if (seconds == null || !Number.isFinite(seconds)) return '—';
  if (seconds < 60)    return `${Math.floor(seconds)}s`;
  if (seconds < 3600)  return `${Math.floor(seconds / 60)}m ${Math.floor(seconds % 60)}s`;
  return `${Math.floor(seconds / 3600)}h ${Math.floor((seconds % 3600) / 60)}m`;
}

function fmtMoney(n) {
  if (n == null) return '—';
  const v = Number(n);
  if (v >= 1000000) return `$${(v / 1000000).toFixed(1)}M`;
  if (v >= 1000)    return `$${(v / 1000).toFixed(1)}k`;
  return `$${v.toFixed(0)}`;
}

function closeRateTone(rate) {
  if (rate >= 15) return 'bg-emerald-100 text-emerald-900';
  if (rate >= 5)  return 'bg-amber-100 text-amber-900';
  return 'bg-slate-100 text-slate-700';
}

function speedTone(seconds) {
  if (seconds == null)     return 'text-slate-500';
  if (seconds < 120)       return 'text-emerald-700';
  if (seconds < 300)       return 'text-amber-700';
  return 'text-red-700';
}

const SOURCE_LABELS = {
  facebook_ad:          'FB Ad',
  referral_from_client: 'Referral',
  sep_trigger:          'SEP',
  aep_campaign:         'AEP',
  organic_web:          'Organic',
  purchased_list:       'Purchased',
  csv_import:           'CSV',
  unknown:              'Unknown',
};

// Local versions of the endpoint's aggregators — used in demo mode so
// the tab renders realistic numbers without a network call. Kept in
// sync with the shape from api/subscriber/agency-analytics.js.
function computeDemoAnalytics(leads, policies) {
  const closeRateBySource = (() => {
    const buckets = new Map();
    for (const l of leads) {
      const key = l.source_tag || 'unknown';
      const b = buckets.get(key) || { source_tag: key, total: 0, sold: 0, transferred: 0, booked: 0, working: 0, new: 0 };
      b.total++;
      const stage = l.stage === 'appointment_set' ? 'booked' : l.stage;
      if (stage === 'sold')        b.sold++;
      if (stage === 'transferred') b.transferred++;
      if (stage === 'booked')      b.booked++;
      if (stage === 'working')     b.working++;
      if (stage === 'new')         b.new++;
      buckets.set(key, b);
    }
    return Array.from(buckets.values()).map(b => ({
      ...b,
      close_rate:   b.total > 0 ? Math.round((b.sold + b.transferred) / b.total * 1000) / 10 : 0,
      working_rate: b.total > 0 ? Math.round((b.booked + b.working)   / b.total * 100)  : 0,
    })).sort((a, b) => b.total - a.total);
  })();
  const speedBySource = (() => {
    const groups = new Map();
    for (const l of leads) {
      if (l.speed_to_contact_seconds == null) continue;
      const key = l.source_tag || 'unknown';
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push(Number(l.speed_to_contact_seconds));
    }
    const perc = (sorted, p) => sorted.length ? sorted[Math.min(sorted.length - 1, Math.floor(sorted.length * p))] : null;
    return Array.from(groups).map(([source, arr]) => {
      const sorted = arr.slice().sort((a, b) => a - b);
      const over = sorted.filter(x => x > 300);
      return {
        source_tag: source,
        count:      sorted.length,
        p50:        perc(sorted, 0.5),
        p90:        perc(sorted, 0.9),
        over_5min_count: over.length,
        over_5min_pct:   Math.round(over.length / sorted.length * 100),
      };
    }).sort((a, b) => b.count - a.count);
  })();
  const mrr = (() => {
    const active = policies.filter(p => ['active','pending'].includes(p.status));
    const total = active.reduce((a, p) => a + Number(p.monthly_commission || 0), 0);
    const buckets = new Map();
    for (const p of active) {
      const key = String(p.carrier || 'unknown').trim() || 'unknown';
      const b = buckets.get(key) || { carrier: key, monthly_commission: 0, policy_count: 0 };
      b.monthly_commission += Number(p.monthly_commission || 0);
      b.policy_count++;
      buckets.set(key, b);
    }
    const carriers = Array.from(buckets.values()).map(b => ({
      ...b,
      pct_of_total: total > 0 ? Math.round(b.monthly_commission / total * 1000) / 10 : 0,
      cliff_alert:  (total > 0 && b.monthly_commission / total * 100 >= 60) ? 'crit' :
                    (total > 0 && b.monthly_commission / total * 100 >= 40) ? 'warn' : null,
      annual_commission_at_risk: Math.round(b.monthly_commission * 12),
    })).sort((a, b) => b.monthly_commission - a.monthly_commission);
    return { total_mrr: Math.round(total * 100) / 100, total_arr: Math.round(total * 12), total_policies: active.length, carriers };
  })();
  return {
    ok: true,
    generated_at: new Date().toISOString(),
    scope: { lead_count: leads.length, policy_count: policies.length },
    close_rate_by_source: closeRateBySource,
    speed_percentiles_by_source: speedBySource,
    mrr_concentration: mrr,
  };
}

export default function AgencyAnalyticsView({ currentUser, showToast = () => {} }) {
  const [data, setData]       = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const isDemo         = useIsDemoMode();
  const demoLeads      = useDemoLeads();
  const demoPolicies   = useDemoHealthPolicies();

  const refresh = React.useCallback(async () => {
    setLoading(true);
    if (isDemo && Array.isArray(demoLeads) && Array.isArray(demoPolicies)) {
      setData(computeDemoAnalytics(demoLeads, demoPolicies));
      setLoading(false);
      return;
    }
    const { ok, body } = await apiFetch('/api/subscriber/agency-analytics');
    if (ok) setData(body);
    else    showToast(`Load failed: ${body?.error || 'unknown'}`);
    setLoading(false);
  }, [showToast, isDemo, demoLeads, demoPolicies]);

  React.useEffect(() => { refresh(); }, [refresh]);

  if (loading && !data) {
    return <div className="p-6 text-sm text-slate-500">Loading agency analytics…</div>;
  }
  if (!data) {
    return <div className="p-6 text-sm text-slate-500">No data.</div>;
  }

  const totalMrr    = data.mrr_concentration?.total_mrr ?? 0;
  const totalArr    = data.mrr_concentration?.total_arr ?? 0;
  const critAlert   = data.mrr_concentration?.carriers?.find(c => c.cliff_alert === 'crit');
  const warnAlert   = data.mrr_concentration?.carriers?.find(c => c.cliff_alert === 'warn');

  return (
    <div className="mx-auto max-w-6xl p-6 space-y-6">
      <header>
        <div className="flex items-center gap-2">
          <h1 className="text-xl font-bold text-slate-900">Agency Analytics</h1>
          <span className="rounded-full bg-fuchsia-100 px-2 py-0.5 text-[0.6rem] font-bold uppercase tracking-wider text-fuchsia-900">
            10K-scale KPIs
          </span>
        </div>
        <p className="mt-1 text-sm text-slate-500">
          Three questions every 10K-client shop should ask daily: where's the money coming
          from, how fast are we hunting, and am I one carrier exit away from a bad quarter.
        </p>
      </header>

      {/* Concentration alerts */}
      {critAlert && (
        <div className="rounded-lg border-2 border-red-500 bg-red-50 p-4">
          <div className="text-sm font-bold text-red-900">
            🚨 CARRIER EXIT RISK — {critAlert.carrier} is {critAlert.pct_of_total}% of your MRR
          </div>
          <div className="mt-1 text-xs text-red-900">
            If {critAlert.carrier} pulls out of your markets or slashes commissions, you'll
            lose <strong>{fmtMoney(critAlert.annual_commission_at_risk)}/yr</strong> overnight.
            Diversify onto at least one carrier per state. Don't wait until the letter arrives.
          </div>
        </div>
      )}
      {!critAlert && warnAlert && (
        <div className="rounded-lg border border-amber-400 bg-amber-50 p-3">
          <div className="text-sm font-semibold text-amber-900">
            ⚠️ Carrier concentration: {warnAlert.carrier} at {warnAlert.pct_of_total}% of MRR
          </div>
          <div className="mt-1 text-xs text-amber-900">
            Not critical yet, but watch this line. Any carrier ≥40% is a business-continuity
            watch item.
          </div>
        </div>
      )}

      {/* KPI strip */}
      <div className="grid grid-cols-3 gap-3">
        <div className="rounded-lg border border-slate-200 bg-white p-4">
          <div className="text-[0.6rem] font-bold uppercase tracking-wider text-slate-500">MRR</div>
          <div className="text-2xl font-bold">{fmtMoney(totalMrr)}</div>
          <div className="text-xs text-slate-500">{fmtMoney(totalArr)}/yr projected</div>
        </div>
        <div className="rounded-lg border border-slate-200 bg-white p-4">
          <div className="text-[0.6rem] font-bold uppercase tracking-wider text-slate-500">Policies</div>
          <div className="text-2xl font-bold">{data.mrr_concentration?.total_policies || 0}</div>
          <div className="text-xs text-slate-500">active + pending</div>
        </div>
        <div className="rounded-lg border border-slate-200 bg-white p-4">
          <div className="text-[0.6rem] font-bold uppercase tracking-wider text-slate-500">Carriers</div>
          <div className="text-2xl font-bold">{data.mrr_concentration?.carriers?.length || 0}</div>
          <div className="text-xs text-slate-500">
            {critAlert ? '🚨 concentration risk' : warnAlert ? '⚠️ concentration watch' : '✅ diversified'}
          </div>
        </div>
      </div>

      {/* Close rate by source */}
      <section className="rounded-lg border border-slate-200 bg-white p-4">
        <h2 className="text-base font-semibold text-slate-900">Where is my money coming from?</h2>
        <p className="text-xs text-slate-500 mt-0.5">Close rate by lead source. Cost-per-CLOSED-lead beats cost-per-lead.</p>
        <div className="mt-3 overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-slate-200 text-left uppercase tracking-wider text-slate-500">
                <th className="py-1.5 pr-3">Source</th>
                <th className="py-1.5 pr-3 text-right">Total</th>
                <th className="py-1.5 pr-3 text-right">Sold</th>
                <th className="py-1.5 pr-3 text-right">Working</th>
                <th className="py-1.5 pr-3 text-right">Close rate</th>
              </tr>
            </thead>
            <tbody>
              {(data.close_rate_by_source || []).map(row => (
                <tr key={row.source_tag} className="border-b border-slate-100">
                  <td className="py-1.5 pr-3 font-semibold">{SOURCE_LABELS[row.source_tag] || row.source_tag}</td>
                  <td className="py-1.5 pr-3 text-right">{row.total}</td>
                  <td className="py-1.5 pr-3 text-right">{row.sold + row.transferred}</td>
                  <td className="py-1.5 pr-3 text-right">{row.booked + row.working}</td>
                  <td className="py-1.5 pr-3 text-right">
                    <span className={`inline-block rounded-full px-2 py-0.5 font-bold ${closeRateTone(row.close_rate)}`}>
                      {row.close_rate}%
                    </span>
                  </td>
                </tr>
              ))}
              {(!data.close_rate_by_source || data.close_rate_by_source.length === 0) && (
                <tr><td colSpan="5" className="py-6 text-center text-slate-400 italic">No leads yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      {/* Speed to contact */}
      <section className="rounded-lg border border-slate-200 bg-white p-4">
        <h2 className="text-base font-semibold text-slate-900">How fast are we hunting?</h2>
        <p className="text-xs text-slate-500 mt-0.5">
          p50 = median speed-to-contact; p90 = worst 10%. Insurance leads decay at 5 min.
        </p>
        <div className="mt-3 overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-slate-200 text-left uppercase tracking-wider text-slate-500">
                <th className="py-1.5 pr-3">Source</th>
                <th className="py-1.5 pr-3 text-right">Contacted</th>
                <th className="py-1.5 pr-3 text-right">p50</th>
                <th className="py-1.5 pr-3 text-right">p90</th>
                <th className="py-1.5 pr-3 text-right">Over 5-min SLA</th>
              </tr>
            </thead>
            <tbody>
              {(data.speed_percentiles_by_source || []).map(row => (
                <tr key={row.source_tag} className="border-b border-slate-100">
                  <td className="py-1.5 pr-3 font-semibold">{SOURCE_LABELS[row.source_tag] || row.source_tag}</td>
                  <td className="py-1.5 pr-3 text-right">{row.count}</td>
                  <td className={`py-1.5 pr-3 text-right font-mono font-bold ${speedTone(row.p50)}`}>{fmtDuration(row.p50)}</td>
                  <td className={`py-1.5 pr-3 text-right font-mono ${speedTone(row.p90)}`}>{fmtDuration(row.p90)}</td>
                  <td className="py-1.5 pr-3 text-right">
                    <span className={`inline-block rounded px-1.5 py-0.5 font-semibold ${row.over_5min_pct >= 30 ? 'bg-red-100 text-red-900' : 'text-slate-700'}`}>
                      {row.over_5min_pct}% ({row.over_5min_count})
                    </span>
                  </td>
                </tr>
              ))}
              {(!data.speed_percentiles_by_source || data.speed_percentiles_by_source.length === 0) && (
                <tr><td colSpan="5" className="py-6 text-center text-slate-400 italic">No contacted leads yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      {/* MRR by carrier */}
      <section className="rounded-lg border border-slate-200 bg-white p-4">
        <h2 className="text-base font-semibold text-slate-900">Am I about to lose the business?</h2>
        <p className="text-xs text-slate-500 mt-0.5">
          MRR concentration by carrier. Alerts at ≥40% (WARN) and ≥60% (CRIT). Carrier exit
          at CRIT levels can end the shop.
        </p>
        <div className="mt-3 overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-slate-200 text-left uppercase tracking-wider text-slate-500">
                <th className="py-1.5 pr-3">Carrier</th>
                <th className="py-1.5 pr-3 text-right">Policies</th>
                <th className="py-1.5 pr-3 text-right">MRR</th>
                <th className="py-1.5 pr-3 text-right">% of Total</th>
                <th className="py-1.5 pr-3 text-right">ARR at risk</th>
                <th className="py-1.5 pr-3 text-right">Alert</th>
              </tr>
            </thead>
            <tbody>
              {(data.mrr_concentration?.carriers || []).map(row => (
                <tr key={row.carrier} className="border-b border-slate-100">
                  <td className="py-1.5 pr-3 font-semibold">{row.carrier}</td>
                  <td className="py-1.5 pr-3 text-right">{row.policy_count}</td>
                  <td className="py-1.5 pr-3 text-right font-mono">{fmtMoney(row.monthly_commission)}</td>
                  <td className="py-1.5 pr-3 text-right">
                    <div className="inline-flex items-center gap-2">
                      <div className="w-16 bg-slate-100 rounded-full h-1.5 overflow-hidden">
                        <div
                          className={`h-full ${row.cliff_alert === 'crit' ? 'bg-red-500' : row.cliff_alert === 'warn' ? 'bg-amber-500' : 'bg-emerald-500'}`}
                          style={{ width: `${Math.min(100, row.pct_of_total)}%` }}
                        />
                      </div>
                      <span className="font-bold">{row.pct_of_total}%</span>
                    </div>
                  </td>
                  <td className="py-1.5 pr-3 text-right font-mono">{fmtMoney(row.annual_commission_at_risk)}</td>
                  <td className="py-1.5 pr-3 text-right">
                    {row.cliff_alert === 'crit' && (
                      <span className="rounded bg-red-100 px-1.5 py-0.5 text-[0.55rem] font-bold uppercase text-red-900">CRIT</span>
                    )}
                    {row.cliff_alert === 'warn' && (
                      <span className="rounded bg-amber-100 px-1.5 py-0.5 text-[0.55rem] font-bold uppercase text-amber-900">WARN</span>
                    )}
                    {!row.cliff_alert && <span className="text-[0.55rem] text-slate-400">OK</span>}
                  </td>
                </tr>
              ))}
              {(!data.mrr_concentration?.carriers || data.mrr_concentration.carriers.length === 0) && (
                <tr><td colSpan="6" className="py-6 text-center text-slate-400 italic">
                  No policies yet — import a carrier statement to populate.
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      <div className="text-[0.65rem] text-slate-400 text-right">
        Generated {data.generated_at ? new Date(data.generated_at).toLocaleString() : '—'} · {data.scope?.lead_count ?? 0} leads · {data.scope?.policy_count ?? 0} policies
      </div>
    </div>
  );
}
